import React from "react";
import { createInertiaApp } from "@inertiajs/react";
import { createRoot } from "react-dom/client";
import Honeybadger from "@honeybadger-io/js";
import { HoneybadgerErrorBoundary } from "@honeybadger-io/react";
import CustomErrorBoundaryComponent from "./custom_error_boundary";
import "../utils/axios_setup";
import { initMobiLoudPush } from "../mobiloud";
import {
  initCapacitorPush,
  registerBackButton,
  initNativeUi,
  initKeyboard,
  initOfflineWatch,
  registerResumeRefresh,
  registerCableReconnect,
  registerPullToRefresh,
  registerDeepLinks,
} from "../capacitor";
import { initializeMultiAccount } from "../utils/multi_account_init";
import { initAppNotificationUrlParamHook } from "../utils/app_notification_url_param";
import { hardReload } from "../utils/hard_reload";

document.addEventListener("DOMContentLoaded", () => {
  // Initialize native push notifications. Each no-ops outside its own shell, so
  // both can be called during the MobiLoud → Capacitor cutover.
  initMobiLoudPush();
  initCapacitorPush();
  // Route Android's back button to in-app history (no-op off Android/Capacitor).
  registerBackButton();
  // Native keyboard UX: keyboard-open class + keep chat pinned to newest
  // message (no-op off the Capacitor shell).
  initKeyboard();
  // Show a native offline banner when connectivity drops (no-op off-shell).
  initOfflineWatch();
  // Refresh the web app when reopened after a long background (no-op off-shell).
  registerResumeRefresh();
  // Reopen the ActionCable socket on foreground so chat/badges resume live
  // updates after the WebView suspended it (no-op off the Capacitor shell).
  registerCableReconnect();
  // Pull-to-refresh the current page from the top of the main scroller
  // (no-op off the Capacitor shell).
  registerPullToRefresh();
  // Route incoming Universal/App Links to the in-app router (no-op off-shell).
  registerDeepLinks();

  // Initialize multi-account support (adds current session to localStorage if enabled)
  initializeMultiAccount();

  // Suppress benign ResizeObserver errors from browser console
  // These occur frequently with Material UI and don't indicate real problems
  window.addEventListener("error", (event) => {
    if (event.message?.includes("ResizeObserver loop")) {
      event.stopImmediatePropagation();
    }
  });

  const honeybadgerEnvironment = window.aws_env?.trim() || window.environment;

  const honeybadger = Honeybadger.configure({
    apiKey: "bcc95794",
    environment: honeybadgerEnvironment,
    // Uncomment to enable reporting in development
    // reportData: true,
  });

  if (window._tkcCurrentUserId) {
    honeybadger.setContext({
      user_id: window._tkcCurrentUserId,
      user_email: window.bloc_user_email,
      // Attribution for churn analysis (guarded for nil — a client with no
      // company or no active primary coach).
      company_id: window._tkcCompanyId ?? undefined,
      coach_id: window._tkcPrimaryCoachId ?? undefined,
    });
  }

  const ignorePatterns = [
    /This video will be available for viewing shortly/i,
    /ResizeObserver loop limit exceeded/i,
    /ResizeObserver loop completed with undelivered notifications/i,
  ];

  Honeybadger.beforeNotify(function (notice) {
    if (ignorePatterns.some((p) => p.test(notice.message))) {
      return false;
    }
  });

  const el = document.querySelector("div#app");

  if (el) {
    createInertiaApp({
      resolve: async (name) => {
        // One-shot reload guard per URL per tab (see missing-name branch).
        // Constructed up front so the SUCCESS path can clear it: after a
        // successful recovery the marker must not linger, or a later
        // intermittent glitch on this URL would skip self-heal and hard-fail
        // (and a stray window.name suffix could confuse tab-id consumers).
        const guardKey = `inertia-resolve-reloaded:${window.location.href}`;
        const nameMark = `__tkc_inertia_reloaded__:${window.location.href};`;
        const reloadGuard = {
          isSet() {
            let inStorage = false;
            try {
              inStorage = window.sessionStorage.getItem(guardKey) !== null;
            } catch {
              // unreadable — rely on window.name below
            }
            const inName =
              typeof window.name === "string" && window.name.includes(nameMark);
            return inStorage || inName;
          },
          set() {
            try {
              window.sessionStorage.setItem(guardKey, "1");
            } catch {
              // unwritable — window.name below is the durable marker
            }
            if (
              typeof window.name !== "string" ||
              !window.name.includes(nameMark)
            ) {
              window.name = `${
                typeof window.name === "string" ? window.name : ""
              }${nameMark}`;
            }
          },
          clear() {
            try {
              window.sessionStorage.removeItem(guardKey);
            } catch {
              // nothing to do — storage marker unreachable anyway
            }
            if (typeof window.name === "string") {
              window.name = window.name.split(nameMark).join("");
            }
          },
        };

        if (!name) {
          // #4589 (follow-on to #4499): a visit arrived whose page payload has
          // no component name — malformed/non-Inertia response (stale bundle,
          // auth redirect swallowed into a visit, or edge-cached shell).
          // Throwing here surfaced as an unhandled rejection for the user 28+
          // times in HB. Self-heal instead: a full reload always produces a
          // server-rendered page with a valid data-page.
          //
          // Structured, non-sensitive context only — no raw payload snippets
          // (props can carry customer data; HB is third-party telemetry).
          const raw = document.querySelector("div#app")?.dataset?.page;
          let parsed = null;
          try {
            parsed = raw ? JSON.parse(raw) : null;
          } catch {
            // raw exists but is not JSON — dataPageParses:false says enough.
          }
          const context = {
            href: window.location.href,
            dataPageLength: raw?.length ?? 0,
            dataPageParses: parsed !== null,
            dataPageComponent: parsed?.component ?? null,
            dataPageUrl: parsed?.url ?? null,
            dataPageVersion: parsed?.version ?? null,
          };

          if (!reloadGuard.isSet()) {
            reloadGuard.set();
            honeybadger.notify(
              "Inertia resolve: page name missing — hard-reloading to recover",
              { context },
            );
            hardReload();
            // Never resolves — the reload takes over before this matters.
            return new Promise(() => {});
          }

          // Second hit for the same URL in this tab: the reload did not fix
          // it. Clear the guard (so a later real navigation may retry) and
          // fail fast — the error boundary beats an infinite refresh loop.
          reloadGuard.clear();
          honeybadger.notify(
            "Inertia resolve: page name still missing after reload — failing fast",
            { context },
          );
          throw new Error(
            "Inertia resolve: page name missing after reload — failing fast to error boundary",
          );
        }
        // Successful resolve: clear any lingering marker so a FUTURE
        // intermittent missing-name on this URL self-heals again instead of
        // failing fast (Mecha-Ben r4).
        reloadGuard.clear();
        const module = await import(`../pages/${name}`);
        return module.default;
      },
      setup({ el, App, props }) {
        createRoot(el).render(
          <HoneybadgerErrorBoundary
            honeybadger={honeybadger}
            ErrorComponent={CustomErrorBoundaryComponent}
          >
            <App {...props} />
          </HoneybadgerErrorBoundary>,
        );
        // After the first page mounts: hide the launch splash on real content
        // and sync the native status bar to the theme (no-op off the Capacitor
        // shell). Done here (not on DOMContentLoaded) so the splash hides over
        // painted app content, avoiding a white flash.
        initNativeUi();
      },
    });

    // notifications_v2 push-tap → mark-read sync (PR-N1). Reads
    // ?app_notification_id=N from the URL on full page load AND on every
    // Inertia client-side navigation, fires PATCH mark_as_read, strips the
    // param so a back-nav doesn't re-fire. Half B (push dismiss) ships in
    // a later PR.
    initAppNotificationUrlParamHook();
  }
});
